/**
 * useResizeObserver — observe element-size changes with a stable callback.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { RefCallback, RefObject } from "react";
import { useThrottledCallback } from "./useThrottledCallback.js";

export interface UseResizeObserverOptions {
  readonly box?: ResizeObserverBoxOptions;
  readonly disabled?: boolean;
  readonly throttleMs?: number;
}

export type ResizeObserverHandler<TElement extends Element> = (
  entry: ResizeObserverEntry,
  target: TElement,
) => void;

export function useResizeObserver<TElement extends Element>(
  onResize: ResizeObserverHandler<TElement>,
  options?: UseResizeObserverOptions,
): RefCallback<TElement>;
export function useResizeObserver<TElement extends Element>(
  targetRef: RefObject<TElement | null>,
  onResize: ResizeObserverHandler<TElement>,
  options?: UseResizeObserverOptions,
): void;

export function useResizeObserver<TElement extends Element>(
  first: RefObject<TElement | null> | ResizeObserverHandler<TElement>,
  second?: ResizeObserverHandler<TElement> | UseResizeObserverOptions,
  third?: UseResizeObserverOptions,
): RefCallback<TElement> | void {
  const usesExternalRef = typeof first !== "function";
  const onResize = usesExternalRef ? (second as ResizeObserverHandler<TElement>) : first;
  const options = usesExternalRef ? third : (second as UseResizeObserverOptions | undefined);
  const [callbackTarget, setCallbackTarget] = useState<TElement | null>(null);
  const externalTarget = usesExternalRef ? first.current : null;
  const observedTarget = usesExternalRef ? externalTarget : callbackTarget;

  const latestOnResize = useRef<ResizeObserverHandler<TElement>>(onResize);
  latestOnResize.current = onResize;
  const throttledResize = useThrottledCallback((entry: ResizeObserverEntry, target: TElement) => {
    latestOnResize.current(entry, target);
  }, options?.throttleMs ?? 50);

  const callbackRef = useCallback<RefCallback<TElement>>((node) => {
    setCallbackTarget(node);
  }, []);

  useEffect(() => {
    if (options?.disabled) return undefined;
    if (typeof ResizeObserver === "undefined") return undefined;
    if (observedTarget === null) return undefined;
    const target = observedTarget;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry === undefined) return;
      throttledResize(entry, target);
    });
    if (options?.box === undefined) {
      observer.observe(target);
    } else {
      observer.observe(target, { box: options.box });
    }
    return (): void => {
      observer.disconnect();
    };
  }, [observedTarget, options?.box, options?.disabled, throttledResize]);

  if (usesExternalRef) return undefined;
  return callbackRef;
}
